import {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
  ReactNode,
  KeyboardEvent,
  MouseEvent,
} from "react";
import cn from "classnames";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import { ControlledProps } from "../../interfaces";
import { SectionNavigationContextType } from "../../section-navigation-context";
import buildGetToggleProps from "./build-get-toggle-props";

const BASE_CLASS = "eb-section-nav";

export interface RegisteredListItem {
  index: number | string;
  ref: ReactNode;
}

const spreadableProp = (prop: any | any[]) =>
  Array.isArray(prop) ? prop : [prop];

const useList = () => {
  const initialList: RegisteredListItem[] = [];
  const registeredLists = useRef(initialList);

  const registerList = useCallback((props: RegisteredListItem) => {
    const listIsRegistered = registeredLists.current.find(
      ({ index: currentIndex }) => currentIndex === props.index,
    );

    if (!listIsRegistered) {
      registeredLists.current = [...registeredLists.current, props];
    }
  }, []);

  return [registeredLists, registerList] as const;
};

const useOpenLists = (initialOpenLists: (string | number)[] = []) => {
  const [openList, setOpenList] = useState(initialOpenLists);

  const toggleOpenList = useCallback(
    (index: number | string): void => {
      const openIndex = openList.indexOf(index);

      if (openIndex === -1) {
        setOpenList([...openList, index]);
        return;
      }

      setOpenList([
        ...openList.slice(0, openIndex),
        ...openList.slice(openIndex + 1),
      ]);
    },
    [openList],
  );

  return [openList, toggleOpenList] as const;
};

const useSectionNavigationUtils = (
  controlledProps: Partial<ControlledProps> = {},
): SectionNavigationContextType => {
  const {
    toggleExpandedLabel,
    toggleCollapsedLabel,
    currentItemId: newCurrentItemId,
    isFlat,
    uniqueName: consumerUniqueName,
    consumerBlockClassName,
  } = controlledProps;

  const [uniqueName, setUniqueName] = useState(consumerUniqueName);
  const [isAutogenerated, setAutogenerated] = useState(false);
  const [registeredLists, registerList] = useList();
  const [openedLists, toggleOpenList] = useOpenLists();
  const [currentItemId, setCurrentItemId] = useState(
    isFlat ? null : newCurrentItemId || null,
  );

  const getBlocksWith = useCallback(
    (props) =>
      createBemBlockBuilder([
        BASE_CLASS,
        ...spreadableProp(consumerBlockClassName),
      ])(props),
    [consumerBlockClassName],
  );

  useEffect(() => {
    if (isFlat) {
      setCurrentItemId(null);
      return;
    }
    if (!isFlat && newCurrentItemId && currentItemId !== newCurrentItemId) {
      setCurrentItemId(newCurrentItemId);
    }
  }, [currentItemId, newCurrentItemId, isFlat]);

  useEffect(() => {
    // If consumer unique name is not blank and unique name is autogenerated, set it
    if (isAutogenerated && consumerUniqueName) {
      setUniqueName(consumerUniqueName);
      setAutogenerated(false);
    }

    // If unique name is not autogenerated and unique name does not equal consumer unique name, set it
    if (!isAutogenerated && consumerUniqueName !== uniqueName) {
      setUniqueName(consumerUniqueName);
      setAutogenerated(false);
    }

    // If unique name is blank and not autogenerated, randomize it
    if (!isAutogenerated && !uniqueName) {
      setUniqueName(`eb-${String(Math.random()).slice(2, 7)}`);
      setAutogenerated(true);
    }
  }, [consumerUniqueName, uniqueName, isAutogenerated]);

  /**
   * refCallback
   * Provides a way to store a node with its current index.
   *
   * @param {number} index
   */
  const refCallback = useCallback(
    (index: number | string) => {
      const ref: { current: null | ReactNode } = { current: null };
      return (node: ReactNode | null) => {
        if (node) {
          ref.current = node;
          registerList({ index, ref });
        }
      };
    },
    [registerList],
  );

  /**
   * getListProps
   *
   * @param {boolean} isSubsection
   * @param {boolean} isFlat
   * @param {number} index
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns Expected props for the list.
   */
  const getListProps = useCallback(
    ({
      isSubsection,
      index,
      className: consumerClassName,
      ...consumerProps
    }) => {
      const isOpen = openedLists.indexOf(index) !== -1;

      return {
        className: cn(
          getBlocksWith("__list"),
          isSubsection && getBlocksWith("__list--subsection"),
          (isOpen || isFlat) && getBlocksWith("__list--open"),
          isFlat && getBlocksWith("__list--flat"),
          ...spreadableProp(consumerClassName),
        ),
        ref: refCallback(index),
        id: (isSubsection && `${uniqueName}-${index}`) || undefined,
        role: "list", // explicitly set role. Safari strips ul implicit role=list when list-style is set to none in css.
        ...consumerProps,
      };
    },
    [openedLists, getBlocksWith, refCallback, uniqueName, isFlat],
  );

  /**
   * getListItemProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {string} childListIndex
   * @param {boolean} hasSubsections
   * @param {object} consumerProps
   *
   * @returns Expected props for the list item.
   */
  const getListItemProps = useCallback(
    ({
      hasSubsections,
      childListIndex,
      id,
      className: consumerClassName,
      ...consumerProps
    }) => {
      const isCurrent = !isFlat && id === currentItemId;
      const isOpen = openedLists.indexOf(childListIndex) !== -1;

      return {
        className: cn(
          getBlocksWith("__list-item"),
          hasSubsections && getBlocksWith("__list-item--has-subsections"),
          isOpen && getBlocksWith("__list-item--open"),
          isCurrent && getBlocksWith("__list-item--current"),
          ...spreadableProp(consumerClassName),
        ),
        ...consumerProps,
      };
    },
    [getBlocksWith, isFlat, currentItemId, openedLists],
  );

  /**
   * getListItemLabelProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {string} dir
   * @param {string} locale
   * @param {function} onClick
   * @param {function} onKeyDown
   * @param {string} tabIndex
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the list item label.
   */
  const getListItemLabelProps = useCallback(
    ({
      id: itemId,
      className: consumerClassName,
      dir,
      locale,
      onClick: consumerOnClick = () => {},
      onKeyDown: consumerOnKeyDown = () => {},
      tabIndex,
      ...consumerProps
    }) => {
      const isCurrent = !isFlat && itemId === currentItemId;

      return {
        className: cn(
          getBlocksWith("__list-item-label"),
          ...spreadableProp(consumerClassName),
        ),
        dir: "auto",
        id: `${uniqueName}-${itemId}-label`,
        lang: locale,
        onClick: (event: MouseEvent) => {
          !isFlat && setCurrentItemId(itemId);
          consumerOnClick({ event, current: itemId });
        },
        onKeyDown: (event: KeyboardEvent) => {
          const { key } = event;

          switch (key) {
            case " ":
            case "Spacebar":
            case "Enter":
              !isFlat && setCurrentItemId(itemId);
          }
          consumerOnKeyDown({ event, current: itemId });
        },
        tabIndex: 0,
        ...(isCurrent ? { "aria-current": "true" } : {}),
        ...consumerProps,
      };
    },
    [currentItemId, getBlocksWith, isFlat, uniqueName],
  );

  /**
   * getAuxiliaryWrapperProps
   *
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns Expected props for the AuxiliaryWrapper.
   */
  const getAuxiliaryWrapperProps = useCallback(
    ({ className: consumerClassName, ...consumerProps }) => {
      return {
        className: cn(
          getBlocksWith("__auxiliary-wrapper"),
          ...spreadableProp(consumerClassName),
        ),
        ...consumerProps,
      };
    },
    [getBlocksWith],
  );

  /**
   * getAuxiliaryIconProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the AuxiliaryIcon.
   */
  const getAuxiliaryIconProps = useCallback(
    ({ className: consumerClassName, id: itemId, ...consumerProps }) => {
      return {
        className: cn(
          getBlocksWith("__auxiliary-icon"),
          ...spreadableProp(consumerClassName),
        ),
        "aria-describedby": `${uniqueName}-${itemId}-label`,
        ...consumerProps,
      };
    },
    [getBlocksWith, uniqueName],
  );

  /**
   * getNavWrapperProps
   *
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the NavWrapper.
   */
  const getNavWrapperProps = useCallback(
    ({ className: consumerClassName, ...consumerProps }) => {
      return {
        "aria-labelledby": `${uniqueName}-header`,
        className: cn(
          getBlocksWith("__nav-wrapper"),
          isFlat && getBlocksWith("__nav-wrapper--flat"),
          ...spreadableProp(consumerClassName),
        ),
        ...consumerProps,
      };
    },
    [getBlocksWith, isFlat, uniqueName],
  );

  const getHeaderProps = useCallback(
    ({ className: consumerClassName, ...consumerProps }) => {
      return {
        id: `${uniqueName}-header`,
        className: cn(
          getBlocksWith("__header"),
          ...spreadableProp(consumerClassName),
        ),
        dir: "auto",
        ...consumerProps,
      };
    },
    [getBlocksWith, uniqueName],
  );

  /**
   * getToggleProps
   *
   * @param {string} id
   * @param {number | string} index
   * @param {Function} onClick
   * @param {Function} onKeyDown
   * @param {string | string[]} className
   */
  const getToggleProps = useCallback(
    ({ index, onClick, onKeyDown, className, id, ...consumerProps }) => {
      return buildGetToggleProps({
        registeredLists,
        openedLists,
        getBlocksWith,
        toggleExpandedLabel,
        toggleCollapsedLabel,
        uniqueName,
        toggleOpenList,
        index,
        id,
        onClick,
        onKeyDown,
        className,
        ...consumerProps,
      });
    },
    [
      registeredLists,
      openedLists,
      getBlocksWith,
      toggleExpandedLabel,
      toggleCollapsedLabel,
      uniqueName,
      toggleOpenList,
    ],
  );

  return useMemo(
    () => ({
      getAuxiliaryIconProps,
      getAuxiliaryWrapperProps,
      getListProps,
      getListItemProps,
      getNavWrapperProps,
      getHeaderProps,
      getToggleProps,
      getListItemLabelProps,
      getBlocksWith,
      openedLists,
      toggleOpenList,
      registeredLists,
      registerList,
      setCurrentItemId,
      currentItemId,
      isFlat,
      uniqueName,
    }),
    [
      currentItemId,
      getAuxiliaryIconProps,
      getAuxiliaryWrapperProps,
      getBlocksWith,
      getHeaderProps,
      getListItemLabelProps,
      getListItemProps,
      getListProps,
      getNavWrapperProps,
      getToggleProps,
      isFlat,
      openedLists,
      registerList,
      registeredLists,
      toggleOpenList,
      uniqueName,
    ],
  );
};

export default useSectionNavigationUtils;
